import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const Payroll = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPayrolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const fetchPayrolls = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll?month=${month}&year=${year}`);
      setPayrolls(res.data);
    } catch (error) {
      console.error(error);
      alert('Failed to fetch payroll data');
    }
    setLoading(false);
  };

  const runPayroll = async () => {
    setLoading(true);
    try {
      await api.post(`/payroll/run`, { month, year });
      alert('Payroll run initiated successfully');
      fetchPayrolls();
    } catch (error) {
      console.error(error);
      alert('Failed to run payroll');
    }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/payroll/${id}/update-status`, { status });
      fetchPayrolls();
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Error updating status: ${msg}`);
    }
  };

  const generatePayslip = async (id) => {
    try {
      await api.post(`/payroll/${id}/payslip`, {});
      fetchPayrolls(); // Refresh to get the payslipId
      alert('Payslip generated successfully in the background!');
    } catch (error) {
      console.error(error);
      alert('Failed to generate payslip');
    }
  };

  const viewPayslip = async (payslipId) => {
    try {
      const dlRes = await api.get(`/me/payslips/${payslipId}/download`);
      window.open(dlRes.data.url, '_blank');
    } catch (error) {
      console.error(error);
      alert('Failed to view payslip');
    }
  };

  const initializeSalaries = async () => {
    setLoading(true);
    try {
      const empRes = await api.get(`/employees`);
      const employees = empRes.data.data || empRes.data;
      
      let count = 0;
      for (const emp of employees) {
        if (emp.role === 'Super Admin') continue; // Optional
        
        try {
          await api.post(`/salary/${emp._id}`, {
            basic: 3000000,
            hra: 1500000,
            allowances: [
              { name: 'Travel', amount: 300000 },
              { name: 'Medical', amount: 200000 }
            ],
            effectiveFrom: '2025-01-01',
            changeReason: 'Initial setup'
          });
          count++;
        } catch (err) {
          // might fail if already exists or permission issues, ignore
        }
      }
      alert(`Successfully initialized salary structures for ${count} employees! You can now Run Payroll.`);
    } catch (error) {
      console.error(error);
      alert('Failed to initialize salaries. Make sure you are an Admin.');
    }
    setLoading(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Payroll Management</h1>
        <div className="flex flex-wrap items-center gap-4">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="border border-gray-300 rounded px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
          </select>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-24 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button onClick={runPayroll} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium transition duration-150 ease-in-out shadow-sm w-full md:w-auto">
            Run Payroll
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Working Days</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid Days</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">LOP Days</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Gross Salary</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Net Salary</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payrolls.length === 0 ? (
                  <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500">No payroll records found for this month.</td></tr>
                ) : (
                  payrolls.map(p => (
                    <tr key={p._id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{p.employeeId?.firstName} {p.employeeId?.lastName}</div>
                        <div className="text-xs text-gray-500">{p.employeeId?.employeeId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.workingDays}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.paidDays}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-red-600 font-medium">{p.lopDays}</span>
                        {p.sandwichDays?.length > 0 && <div className="text-xs text-gray-500 mt-1">({p.sandwichDays.length} Sandwiched)</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">₹{(p.grossSalary / 100).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">₹{(p.netSalary / 100).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${p.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' : 
                            p.status === 'CALCULATED' ? 'bg-blue-100 text-blue-800' : 
                            p.status === 'UNDER_REVIEW' ? 'bg-yellow-100 text-yellow-800' : 
                            p.status === 'PROCESSED' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        {p.status === 'DRAFT' && <button disabled={loading} onClick={() => updateStatus(p._id, 'CALCULATED')} className="text-blue-600 hover:text-blue-900 mx-2 hover:underline disabled:opacity-50">Calculate</button>}
                        {p.status === 'CALCULATED' && <button disabled={loading} onClick={() => updateStatus(p._id, 'UNDER_REVIEW')} className="text-yellow-600 hover:text-yellow-900 mx-2 hover:underline disabled:opacity-50">Review</button>}
                        {p.status === 'UNDER_REVIEW' && <button disabled={loading} onClick={() => updateStatus(p._id, 'PROCESSED')} className="text-green-600 hover:text-green-900 mx-2 hover:underline disabled:opacity-50">Process</button>}
                        {p.status === 'PROCESSED' && (
                          <div className="flex justify-center space-x-3">
                            <button disabled={loading} onClick={() => updateStatus(p._id, 'PAID')} className="text-purple-600 hover:text-purple-900 hover:underline disabled:opacity-50">Mark Paid</button>
                            {!p.payslipId ? (
                              <button disabled={loading} onClick={() => generatePayslip(p._id)} className="text-indigo-600 hover:text-indigo-900 hover:underline disabled:opacity-50">Gen Payslip</button>
                            ) : (
                              <>
                                <button disabled={loading} onClick={() => viewPayslip(p.payslipId)} className="text-teal-600 hover:text-teal-900 hover:underline disabled:opacity-50">View Payslip</button>
                                <button disabled={loading} onClick={() => generatePayslip(p._id)} className="text-gray-500 hover:text-gray-800 hover:underline text-xs disabled:opacity-50" title="Regenerate with latest template">↻ Regen</button>
                              </>
                            )}
                          </div>
                        )}
                        {p.status === 'PAID' && (
                          <div className="flex justify-center space-x-3">
                            {!p.payslipId ? (
                              <button disabled={loading} onClick={() => generatePayslip(p._id)} className="text-indigo-600 hover:text-indigo-900 hover:underline disabled:opacity-50">Gen Payslip</button>
                            ) : (
                              <>
                                <button disabled={loading} onClick={() => viewPayslip(p.payslipId)} className="text-teal-600 hover:text-teal-900 hover:underline disabled:opacity-50">View Payslip</button>
                                <button disabled={loading} onClick={() => generatePayslip(p._id)} className="text-gray-500 hover:text-gray-800 hover:underline text-xs disabled:opacity-50" title="Regenerate with latest template">↻ Regen</button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;
